import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { getDGPennyList } from "../../services/ToolsService";
import { useUserContext } from "../../context/UserContext";
import { isFreeTier } from "../../utils/userUtils";
import { getProductImageUrl } from "../../utils/imageUtils";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const shortDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export default function DGPennyListTool() {
  const { user } = useUserContext();
  const paid = Boolean(user) && !isFreeTier(user);
  const { data, isLoading, isError } = useQuery({
    // Paid accounts get every UPC, so the two views are cached apart.
    queryKey: ["tools", "dg-penny-list", paid],
    queryFn: getDGPennyList,
    staleTime: 15 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-xl bg-white" />
        ))}
      </div>
    );
  }
  if (isError || !data) {
    return <p className="text-slate-600">The penny list couldn't load right now. Try again in a few minutes.</p>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <p className="text-2xl font-extrabold">{data.newThisWeek} new penny items</p>
        <p className="text-sm text-slate-600">
          confirmed since {shortDate(data.weekStart)} · {data.activeTotal.toLocaleString()} active in all
        </p>
      </div>
      {data.items.length === 0 ? (
        <p className="mt-4 text-slate-600">No new penny items confirmed yet this week. Check back after Tuesday.</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {data.items.map((item, index) => (
            <li key={`${item.upc}-${index}`} className="flex items-center gap-3 p-3">
              {item.image ? (
                <img src={getProductImageUrl(item.image, "400")} alt="" loading="lazy" width={48} height={48} className="h-12 w-12 flex-shrink-0 rounded-lg object-contain" />
              ) : (
                <div className="h-12 w-12 flex-shrink-0 rounded-lg bg-slate-100" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold leading-snug">{item.name}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {item.upcMasked ? (
                    <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> UPC {item.upc}</span>
                  ) : (
                    <span className="font-mono">UPC {item.upc}</span>
                  )}
                  {item.regularPrice ? <> · was {money(item.regularPrice)}</> : null} · confirmed {shortDate(item.confirmedAt)}
                </p>
              </div>
              <span className="flex-shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">$0.01</span>
            </li>
          ))}
        </ul>
      )}
      {!paid && data.newThisWeek > data.unmaskedLimit && (
        <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50 p-4">
          <p className="font-semibold text-violet-900">See every UPC and which stores near you have them</p>
          <p className="mt-1 text-sm text-violet-800">A DealHawk Dollar General plan unlocks the full list and shows confirmed penny items at your own stores.</p>
          <Link to="/get-started" className="mt-3 inline-block rounded-xl bg-violet-700 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-800">
            Unlock the full list
          </Link>
        </div>
      )}
    </div>
  );
}
